/**
 * aethereum context — print the room's shared context as paste-in text.
 *
 * The bridge for hosted web builders (v0, Lovable, Bolt, Replit) that can't run
 * a local MCP server: pipe or paste this into the builder's prompt so its agent
 * sees your contracts, decisions, intent, and any collision alerts. Read-only
 * (peek): never advances the alert cursor.
 */

import { loadAutoConfig } from "../hive/auto.js";

export interface ContextOptions {
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

export async function runContext(options: ContextOptions = {}): Promise<void> {
  const cwd = options.cwd ?? process.cwd();
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  const fetchImpl = options.fetchImpl ?? fetch;

  // Probe the underlying config even if AETHEREUM_AUTO=off (this is an explicit read).
  const cfg = loadAutoConfig(cwd, { ...env, AETHEREUM_AUTO: "" });
  if (!cfg) {
    console.error("No Aethereum room configured here. Run 'aethereum init --token <key>' first.");
    process.exit(1);
    return;
  }

  try {
    const res = await fetchImpl(`${cfg.url}/api/sync/context?peek=1`, {
      headers: { Authorization: `Bearer ${cfg.token}` },
      signal: AbortSignal.timeout(5_000),
    });
    if (!res.ok) {
      console.error(
        res.status === 403 || res.status === 401
          ? "Token rejected (revoked or wrong room). Reconnect from the dashboard."
          : `Could not reach Aethereum (${res.status}).`,
      );
      process.exit(1);
      return;
    }
    const body = (await res.json()) as { text?: string };
    if (body.text) {
      // A one-line explainer so the paste-in block is self-describing wherever it lands.
      console.log("# Aethereum shared context — paste this into your web builder's prompt so its agent");
      console.log("# sees the team's intent, contracts, decisions, and any live collision alerts.");
      console.log("");
    }
    console.log(body.text ?? "No shared context yet. You're the first agent here.");
  } catch {
    console.error("Could not reach Aethereum. Check your network.");
    process.exit(1);
  }
}
